import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Sparkles } from "lucide-react";
import { LENGTHS, TIMEZONES, zonedToUtc } from "@/lib/schedule";

type Props = { userId?: string; onQueued: () => void };

export function CreatePostCard({ userId, onQueued }: Props) {
  const navigate = useNavigate();
  const [topic, setTopic] = useState("");
  const [keyword, setKeyword] = useState("");
  const [hint, setHint] = useState("");
  const [length, setLength] = useState(1500);
  const [scheduleIt, setScheduleIt] = useState(false);
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [time, setTime] = useState("09:00");
  const [timezone, setTimezone] = useState("America/New_York");
  const [autoPublish, setAutoPublish] = useState(false);
  const [busy, setBusy] = useState(false);

  const generateNow = async () => {
    if (!topic.trim()) { toast.error("Topic is required"); return; }
    setBusy(true);
    try {
      const { data, error } = await supabase.functions.invoke("generate-blog-post", {
        body: { topic, keyword, length_words: length, editorial_hint: hint || null },
      });
      if (error) throw error;
      const d = data?.draft;
      if (!d?.title || !d?.slug) throw new Error("AI returned an incomplete draft");

      const { data: inserted, error: insErr } = await supabase.from("blog_posts").insert({
        slug: d.slug,
        title: d.title,
        excerpt: d.excerpt ?? "",
        category: d.category ?? "Insights",
        read_time: d.read_time ?? "6 min read",
        meta_title: d.meta_title ?? d.title,
        meta_description: d.meta_description ?? d.excerpt ?? "",
        intro: d.intro ?? "",
        sections: d.sections ?? [],
        faqs: d.faqs ?? [],
        hero_image_url: d.hero_image_url ?? null,
        hero_image_alt: d.hero_image_alt ?? null,
        status: "draft",
        topic,
        keyword,
        author_id: userId ?? null,
      }).select("id").single();
      if (insErr) throw insErr;

      toast.success("Draft generated. Review it before publishing.");
      navigate(`/admin/blog/${inserted.id}`);
    } catch (err: any) {
      toast.error(err.message ?? "Generation failed");
    } finally {
      setBusy(false);
    }
  };

  const schedule = async () => {
    if (!topic.trim()) { toast.error("Topic is required"); return; }
    const at = zonedToUtc(date, time, timezone);
    if (at.getTime() < Date.now()) { toast.error("Pick a date and time in the future"); return; }
    setBusy(true);
    const { error } = await supabase.from("blog_scheduled_posts").insert({
      topic: topic.trim(),
      keyword: keyword.trim() || null,
      editorial_hint: hint.trim() || null,
      length_words: length,
      publish_automatically: autoPublish,
      scheduled_at: at.toISOString(),
      timezone,
      status: "scheduled",
      created_by: userId ?? null,
    });
    setBusy(false);
    if (error) { toast.error(error.message); return; }
    toast.success("Post scheduled");
    setTopic(""); setKeyword(""); setHint("");
    onQueued();
  };

  return (
    <div className="rounded-xl border border-border bg-card p-6">
      <h3 className="font-display text-xl font-bold mb-6 flex items-center gap-2">
        <Sparkles className="h-5 w-5 text-accent" /> New post
      </h3>

      <div className="grid gap-5 md:grid-cols-2">
        <div>
          <Label htmlFor="cp-topic" className="text-xs uppercase tracking-wider text-muted-foreground">Topic / article idea *</Label>
          <Input id="cp-topic" className="mt-1.5" placeholder="Best flooring for Connecticut basements" value={topic} onChange={(e) => setTopic(e.target.value)} />
        </div>
        <div>
          <Label htmlFor="cp-kw" className="text-xs uppercase tracking-wider text-muted-foreground">Primary keyword</Label>
          <Input id="cp-kw" className="mt-1.5" placeholder="basement flooring Danbury CT" value={keyword} onChange={(e) => setKeyword(e.target.value)} />
        </div>
      </div>

      <div className="mt-5 grid gap-5 md:grid-cols-3">
        <div>
          <Label htmlFor="cp-len" className="text-xs uppercase tracking-wider text-muted-foreground">Length</Label>
          <select
            id="cp-len"
            className="mt-1.5 flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            value={length}
            onChange={(e) => setLength(Number(e.target.value))}
          >
            {LENGTHS.map((l) => <option key={l.value} value={l.value}>{l.label}</option>)}
          </select>
        </div>
        <div className="md:col-span-2">
          <Label htmlFor="cp-hint" className="text-xs uppercase tracking-wider text-muted-foreground">Editorial hint (optional)</Label>
          <Textarea id="cp-hint" className="mt-1.5 min-h-[44px]" placeholder="Mention moisture control and LVP options." value={hint} onChange={(e) => setHint(e.target.value)} />
        </div>
      </div>

      <label className="mt-6 flex items-center gap-2 text-sm">
        <Checkbox checked={scheduleIt} onCheckedChange={(v) => setScheduleIt(!!v)} />
        Schedule for a specific date and time
      </label>

      {scheduleIt && (
        <div className="mt-4 grid gap-5 md:grid-cols-4">
          <div>
            <Label htmlFor="cp-date" className="text-xs uppercase tracking-wider text-muted-foreground">Date</Label>
            <Input id="cp-date" type="date" className="mt-1.5" value={date} onChange={(e) => setDate(e.target.value)} />
          </div>
          <div>
            <Label htmlFor="cp-time" className="text-xs uppercase tracking-wider text-muted-foreground">Time</Label>
            <Input id="cp-time" type="time" className="mt-1.5" value={time} onChange={(e) => setTime(e.target.value)} />
          </div>
          <div>
            <Label htmlFor="cp-tz" className="text-xs uppercase tracking-wider text-muted-foreground">Timezone</Label>
            <select
              id="cp-tz"
              className="mt-1.5 flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              value={timezone}
              onChange={(e) => setTimezone(e.target.value)}
            >
              {TIMEZONES.map((tz) => <option key={tz} value={tz}>{tz}</option>)}
            </select>
          </div>
          <label className="flex items-end gap-2 pb-2.5 text-sm">
            <Checkbox checked={autoPublish} onCheckedChange={(v) => setAutoPublish(!!v)} />
            Publish automatically
          </label>
        </div>
      )}

      <div className="mt-6 flex gap-2">
        {scheduleIt ? (
          <Button onClick={schedule} disabled={busy}>{busy ? "Scheduling…" : "Schedule post"}</Button>
        ) : (
          <Button onClick={generateNow} disabled={busy}>{busy ? "Generating…" : "Generate draft now"}</Button>
        )}
      </div>
    </div>
  );
}
